import React, { useEffect, useState } from 'react';
import {
  XMarkIcon,
  FunnelIcon,
  TagIcon,
  BuildingStorefrontIcon,
  CurrencyRupeeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';

const SORT_OPTIONS = [
  { label: 'Newest First', sortBy: 'createdAt', sortOrder: 'desc' },
  { label: 'Oldest First', sortBy: 'createdAt', sortOrder: 'asc' },
  { label: 'Price: Low to High', sortBy: 'price', sortOrder: 'asc' },
  { label: 'Price: High to Low', sortBy: 'price', sortOrder: 'desc' },
  { label: 'Name: A to Z', sortBy: 'name', sortOrder: 'asc' },
  { label: 'Name: Z to A', sortBy: 'name', sortOrder: 'desc' },
];

const labelClassName =
  'block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5';

const HorizontalProductFilters = ({
  categories,
  brands,
  selectedCategory,
  selectedBrand,
  minPrice,
  maxPrice,
  onFilterChange,
  onClearFilters,
  isLoading = false,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  onSortChange,
}) => {
  const [localMinPrice, setLocalMinPrice] = useState(minPrice || '');
  const [localMaxPrice, setLocalMaxPrice] = useState(maxPrice || '');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setLocalMinPrice(minPrice || '');
  }, [minPrice]);

  useEffect(() => {
    setLocalMaxPrice(maxPrice || '');
  }, [maxPrice]);

  const hasActiveFilters = selectedCategory || selectedBrand || minPrice || maxPrice;
  const activeFilterCount = [selectedCategory, selectedBrand, minPrice, maxPrice].filter(Boolean).length;

  const handleMinPriceChange = (value) => {
    setLocalMinPrice(value);
    onFilterChange('minPrice', value);
  };

  const handleMaxPriceChange = (value) => {
    setLocalMaxPrice(value);
    onFilterChange('maxPrice', value);
  };

  const sortedCategories = [...categories].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
  const sortedBrands = [...brands].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  const activeChips = [
    selectedCategory && { key: 'category', label: 'Category', value: selectedCategory },
    selectedBrand && { key: 'brand', label: 'Brand', value: selectedBrand },
    (minPrice || maxPrice) && {
      key: 'price',
      label: 'Price',
      value: `₹${minPrice || '0'} – ₹${maxPrice || '∞'}`,
      clear: () => {
        onFilterChange('minPrice', '');
        onFilterChange('maxPrice', '');
      },
    },
  ].filter(Boolean);

  const removeChip = (chip) => {
    if (chip.clear) {
      chip.clear();
    } else {
      onFilterChange(chip.key, '');
    }
  };

  const sortValue = `${sortBy}:${sortOrder}`;

  const handleSortSelect = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split(':');
    onSortChange?.(newSortBy, newSortOrder);
  };

  return (
    <div className="store-filter-panel overflow-hidden p-0">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        className="md:hidden w-full flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-primary-600 text-white shrink-0">
            <FunnelIcon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-display font-bold text-slate-900">Filters</h2>
            {activeFilterCount > 0 && (
              <p className="text-xs text-slate-500 truncate">
                {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} applied
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {activeFilterCount > 0 && (
            <span className="store-chip py-0.5 text-xs">{activeFilterCount}</span>
          )}
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>

      <div className="hidden md:flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-primary-600 text-white">
            <FunnelIcon className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-display font-bold text-slate-900">Filters</h2>
            <p className="text-xs text-slate-500">Refine products for your export selection</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <span className="store-chip text-xs">{activeFilterCount} active</span>
        )}
      </div>

      {!isExpanded && activeChips.length > 0 && (
        <div className="md:hidden flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-slate-100">
          {activeChips.map((chip) => (
            <button key={chip.key} type="button" onClick={() => removeChip(chip)} className="store-chip text-xs py-1">
              {chip.value}
              <XMarkIcon className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      <div className={`${isExpanded ? 'block' : 'hidden'} md:block p-4 sm:p-5`}>
        <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4 items-end">
          <div className="xl:col-span-2">
            <label htmlFor="shortlist-category" className={labelClassName}>
              <span className="inline-flex items-center gap-1.5">
                <TagIcon className="w-3.5 h-3.5" />
                Category
              </span>
            </label>
            <select
              id="shortlist-category"
              value={selectedCategory}
              onChange={(e) => onFilterChange('category', e.target.value)}
              disabled={isLoading}
              className="admin-input py-2"
            >
              <option value="">All Categories</option>
              {sortedCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="xl:col-span-2">
            <label htmlFor="shortlist-brand" className={labelClassName}>
              <span className="inline-flex items-center gap-1.5">
                <BuildingStorefrontIcon className="w-3.5 h-3.5" />
                Brand
              </span>
            </label>
            <select
              id="shortlist-brand"
              value={selectedBrand}
              onChange={(e) => onFilterChange('brand', e.target.value)}
              disabled={isLoading}
              className="admin-input py-2"
            >
              <option value="">All Brands</option>
              {sortedBrands.map((brand) => (
                <option key={brand.id} value={brand.name}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2 xl:col-span-4">
            <p className={labelClassName}>
              <span className="inline-flex items-center gap-1.5">
                <CurrencyRupeeIcon className="w-3.5 h-3.5" />
                Price Range
              </span>
            </p>
            <div className="flex items-center gap-2">
              <input
                id="shortlist-min-price"
                type="number"
                min="0"
                aria-label="Min. Price"
                value={localMinPrice}
                onChange={(e) => handleMinPriceChange(e.target.value)}
                placeholder="Min"
                className="admin-input py-2"
              />
              <span className="text-xs font-medium text-slate-400 shrink-0">—</span>
              <input
                id="shortlist-max-price"
                type="number"
                min="0"
                aria-label="Max Price"
                value={localMaxPrice}
                onChange={(e) => handleMaxPriceChange(e.target.value)}
                placeholder="Max"
                className="admin-input py-2"
              />
            </div>
          </div>

          <div className="col-span-2 xl:col-span-3">
            <label htmlFor="shortlist-sort" className={labelClassName}>
              <span className="inline-flex items-center gap-1.5">
                <ArrowsUpDownIcon className="w-3.5 h-3.5" />
                Sort
              </span>
            </label>
            <select
              id="shortlist-sort"
              value={sortValue}
              onChange={handleSortSelect}
              className="admin-input py-2"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.label} value={`${option.sortBy}:${option.sortOrder}`}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-1 xl:col-span-1 flex flex-col justify-end">
            <button
              type="button"
              onClick={onClearFilters}
              disabled={!hasActiveFilters}
              title="Clear filters"
              aria-label="Clear filters"
              className="inline-flex items-center justify-center w-full h-[42px] rounded-xl border border-slate-300 bg-white text-slate-600 hover:border-primary-300 hover:text-primary-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="hidden md:flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">Applied</span>
            {activeChips.map((chip) => (
              <button key={chip.key} type="button" onClick={() => removeChip(chip)} className="store-chip text-xs">
                {chip.value}
                <XMarkIcon className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalProductFilters;
